import { TranslationKey } from './../../../../../../types/translation/TranslationKey';
import React, { useContext, useState, useMemo, useEffect } from 'react';
import { t } from '../../../../../../types/translation/Translator';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import { OrderSettings } from '../../../../../../types/order_settings/orderSettings';
import {
  CreateOrderSettingsResponse,
  CreateOrderSettingsVariables,
  GetOrderSettingsResponse,
  GetOrderSettingsVariables,
  OrderSettingsMutations,
  OrderSettingsQueries,
  UpdateOrderSettingsResponse,
  UpdateOrderSettingsVariables,
} from '../../../../../../graphql/orderSettings.graphql';
import LoadingPackage from '../../../../Common/LoadingPackage';
import useMutation from '../../../../../../hooks/useMutation';
import useQuery from '../../../../../../hooks/useQuery';
import { DropdownSelect } from '../../../../../components/Dropdown';
import { OrderUtil } from '../../../../../../types/order';
import { Divider } from '../../../../../components/Divider';
import { OrderSettingsInput } from '../../../../../../types/order_settings';
import { TextInput } from '../../../../../components/TextInput';
import { SaveChangesButton } from '../../../../../components/Button';
import { Checkbox } from '../../../../../components/Checkbox';
import { useToast } from '../../../../../../providers/ToastProvider';
import { ToastType } from '../../../../../components/Toast';
import { FlowType } from '../../../../../../types/order/orderFlow';

export default function OrderSettingsPane() {
  const { currentCompany } = useContext(CompanyContext);

  const [selectedType, setSelectedType] = useState<FlowType>(FlowType.inbound);
  const [settings, setSettings] = useState<Map<FlowType, OrderSettings>>(new Map());

  const { loading } = useQuery<GetOrderSettingsResponse, GetOrderSettingsVariables>(OrderSettingsQueries.get, {
    onCompleted: res => setSettings(new Map(res.orderSettings.data.map(os => [os.type, new OrderSettings(os)]))),
  });

  if (loading) {
    return (
      <div className='content-x flex h-full flex-col items-center justify-center'>
        <LoadingPackage />
      </div>
    );
  }

  return (
    <div className='flex h-full flex-col gap-4'>
      <div className='content-x grid gap-4'>
        <div className='grid gap-2'>
          <p className='page-title'>{t(TranslationKey.orderSettingsPaneTitle).singular.upper}</p>
          <p className='subtitle'>{t(TranslationKey.orderSettingsPaneSubtitle).singular.upper}</p>
        </div>
        <div className='r-grid'>
          <DropdownSelect<FlowType>
            label={t(TranslationKey.orderType).singular.upper}
            items={[FlowType.inbound, FlowType.outbound, FlowType.disposal, FlowType.move]}
            textBuilder={OrderUtil.typeToString}
            onSelect={setSelectedType}
            value={selectedType}
          />
        </div>
        <Divider />
      </div>
      <OrderSettingsView type={selectedType} settings={settings} setSettings={setSettings} />
    </div>
  );
}

interface OrderSettingsViewProps {
  type: FlowType;
  settings: Map<FlowType, OrderSettings>;
  setSettings: (settings: Map<FlowType, OrderSettings>) => void;
}

function OrderSettingsView({ type, settings, setSettings }: OrderSettingsViewProps) {
  const { showToast } = useToast();

  const { currentCompany } = useContext(CompanyContext);
  const original = useMemo(() => settings.get(type), [settings, type]);

  const [input, setInput] = useState(
    new OrderSettingsInput(original || new OrderSettings({ companyId: currentCompany.id, type })),
  );

  useEffect(() => {
    setInput(new OrderSettingsInput(original || new OrderSettings({ companyId: currentCompany.id, type })));
  }, [original]);

  const handleActionComplete = (result: OrderSettings) => {
    setInput(new OrderSettingsInput(result));
    setSettings(new Map(settings.set(result.type, new OrderSettings(result))));
    showToast({ type: ToastType.success, title: t(TranslationKey.orderSettingsUpdatedToastTitle).singular.upper });
  };

  const [create, { loading: createLoading }] = useMutation<CreateOrderSettingsResponse, CreateOrderSettingsVariables>(
    OrderSettingsMutations.create,
    {
      variables: {
        orderSettings: [input.forCreate()],
      },
      onCompleted: response => handleActionComplete(response.createOrderSettings[0]),
    },
  );

  const [update, { loading: updateLoading }] = useMutation<UpdateOrderSettingsResponse, UpdateOrderSettingsVariables>(
    OrderSettingsMutations.update,
    {
      variables: {
        orderSettings: [input.forUpdate()],
      },
      onCompleted: response => handleActionComplete(response.updateOrderSettings[0]),
    },
  );

  const handleSave = () => {
    if (input.id) {
      update();
    } else {
      create();
    }
  };

  return (
    <div className='content-x flex flex-grow flex-col gap-4 overflow-auto'>
      <div className='grid gap-2'>
        <p className='section-title'>{t(TranslationKey.orderNumbers).singular.upper}</p>
        <div className='r-grid'>
          <TextInput
            label={t(TranslationKey.prefix).singular.upper}
            onChange={value => setInput(input.withPrefix(value))}
            value={input.prefix}
            placeholder={t(TranslationKey.prefix).singular.upper}
          />
          <TextInput
            label={t(TranslationKey.suffix).singular.upper}
            onChange={value => setInput(input.withSuffix(value))}
            value={input.suffix}
            placeholder={t(TranslationKey.suffix).singular.upper}
          />
        </div>
        <div className='r-grid'>
          <TextInput
            mandatory
            label={t(TranslationKey.nrOfDigits).singular.upper}
            onChange={value => setInput(input.withDigits(value))}
            value={input.digits}
            type='number'
          />
        </div>
      </div>
      <div className='grid gap-2'>
        <p className='section-title'>{t(TranslationKey.additionalSettings).singular.upper}</p>
        <Checkbox
          label={t(TranslationKey.sendPushNotificationToAssignees).singular.upper}
          onChange={value => setInput(input.withSendPushNotificationToAssignees(value))}
          value={input.sendPushNotificationToAssignees}
        />
      </div>
      <div className='mt-4 flex gap-2'>
        <SaveChangesButton
          onClick={handleSave}
          loading={createLoading || updateLoading}
          disabled={!input.validate() || !input.hasChanges(original)}
        />
      </div>
    </div>
  );
}
